import { LoadingOutlined } from "@ant-design/icons";
import { ResponsiveBar, BarLegendProps } from "@nivo/bar";
import Spin from "antd/lib/spin";
import theme from "antd/lib/theme";
import clsx from "clsx";
import { TFunction } from "i18next";
import { groupBy } from "lodash";
import { useTranslation } from "react-i18next";

import { IBriefAssignmentListQueryResult, IClassroomItem, useBriefAssignmentListQuery } from "@sol/sdk";

type classroomWorkProgressDataType = {
    [key: string]: number;
};

type WorkStatusCounts = {
    assigned: number;
    submitted: number;
    corrected: number;
};

const transformWorkProgressionData = (
    apiData: IBriefAssignmentListQueryResult | null | undefined,
): WorkStatusCounts | null => {
    const assignments = apiData?.["hydra:member"] ?? [];

    if (assignments.length < 1) {
        return null;
    }

    const groups = groupBy(assignments, a => a.state);

    const counts: WorkStatusCounts = {
        assigned: groups.assigned?.length ?? 0,
        submitted: groups.rendered?.length ?? 0,
        corrected: (groups.corrected?.length ?? 0) + (groups.validated?.length ?? 0),
    };

    return Object.values(counts).some(v => v > 0) ? counts : null;
};

type ClassroomWorkProgressProps = {
    className?: string;
    classroom: IClassroomItem;
};

const emptyLegend = (t: TFunction) =>
    ({
        data: [{ label: t("component.dashboard.ClassroomWorkProgress.noData"), color: "transparent" }],
        translateY: 30,
    }) as BarLegendProps;

export function ClassroomWorkProgress({ className, classroom }: ClassroomWorkProgressProps) {
    const [t] = useTranslation();
    const { token } = theme.useToken();

    const colors = [token.colorBorder + "DD", token.colorPrimary + "80", token.colorPrimary];

    const defaultData: classroomWorkProgressDataType = { empty: 100 };

    const { data: apiData, isLoading } = useBriefAssignmentListQuery(
        { filters: { classroom } },
        {
            enabled: !!classroom,
        },
    );

    const workStatusCounts = transformWorkProgressionData(apiData);
    const workProgressData: classroomWorkProgressDataType = (() => {
        if (!workStatusCounts) {
            return defaultData;
        }

        return {
            [t("component.dashboard.ClassroomWorkProgress.assigned")]: workStatusCounts.assigned || 0,
            [t("component.dashboard.ClassroomWorkProgress.submitted")]: workStatusCounts.submitted || 0,
            [t("component.dashboard.ClassroomWorkProgress.corrected")]: workStatusCounts.corrected || 0,
        };
    })();

    return (
        <div className={clsx("h-[87px]", className)}>
            <h3 className="text-gray-800 mb-2 text-lg font-semibold">
                {t("component.dashboard.ClassroomWorkProgress.title")}
            </h3>

            {isLoading && (
                <div className="flex h-16 items-center justify-center">
                    <Spin indicator={<LoadingOutlined spin />} size="large" />
                </div>
            )}

            {!isLoading && (
                <ResponsiveBar
                    data={[workProgressData]}
                    keys={Object.keys(workProgressData)}
                    margin={{ top: 0, right: 0, bottom: 80, left: 0 }}
                    innerPadding={2}
                    padding={0}
                    layout="horizontal"
                    colors={colors}
                    axisTop={null}
                    axisRight={null}
                    axisLeft={null}
                    axisBottom={null}
                    enableGridX={false}
                    enableGridY={false}
                    enableLabel={false}
                    isInteractive={false}
                    borderRadius={4}
                    legends={
                        workProgressData.empty
                            ? [emptyLegend(t)]
                            : [
                                  {
                                      anchor: "bottom",
                                      direction: Object.keys(workProgressData).length > 3 ? "column" : "row",
                                      translateY: 30,
                                      itemsSpacing: 10,
                                      itemWidth: 83,
                                      itemHeight: 20,
                                      symbolSize: 12,
                                      symbolShape: "circle",
                                      symbolSpacing: 4,
                                      dataFrom: "keys",
                                      data: [
                                          ...Object.keys(workProgressData).map((key, index) => ({
                                              id: key,
                                              label: `${key} (${workProgressData[key as keyof typeof workProgressData]})`,
                                              color: colors[index % colors.length],
                                          })),
                                      ],
                                  },
                              ]
                    }
                />
            )}
        </div>
    );
}
